import {
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";

import { useTableDragDropProps } from "../ItemsTableDragDrop.types";

export const useTableDragDrop = <TData extends { id: string }, TValue>({
  columns,
  data,
  sorting,
  setSorting,
  keyForReorder,
  onChange
}: useTableDragDropProps<TData, TValue>) => {
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    onSortingChange: setSorting,
    state: { sorting },
    getRowId: (row) => row.id
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active && over && active.id !== over.id) {
      const fromIndex = data.findIndex((item) => item.id === active.id);
      const toIndex = data.findIndex((item) => item.id === over.id);
      const fromKeyForReorder = keyForReorder
        ? Number(data.find((item) => item.id === active.id)?.[keyForReorder])
        : undefined;
      const toKeyForReorder = keyForReorder
        ? Number(data.find((item) => item.id === over.id)?.[keyForReorder])
        : undefined;

      if (active.id) {
        onChange({
          draggableId: active.id as string,
          fromKeyForReorder,
          toKeyForReorder,
          fromIndex,
          toIndex
        });
      }
    }
  };

  return {
    sensors,
    table,
    collisionDetection: closestCenter,
    modifiers: [restrictToVerticalAxis],
    onDragEnd
  };
};
